import type { SkinData, TestModeConfig } from '@wooorms/shared';
import { ConnectionState } from '../network/NetworkManager';
import { SkinSelector, SkinTab } from './SkinSelector';
import { AccessorySelector, AccessoryType } from './AccessorySelector';
import { EyeProtectionSettings, EyeProtectionLevel } from './EyeProtectionToggle';
import { SoundManager, SoundType } from '../audio/SoundManager';
import { getHelpMenu } from './HelpMenu';
import { getMobileControlsSettings } from './MobileControlsSettings';
import { isTouchDevice } from '../utils/MobileDetection';
import { TestModeSelector } from './TestModeSelector';
import { LocalPartyLobby } from './LocalPartyLobby';
import { PeerJSSignalingProvider, SignalingProvider } from '../network/SignalingProvider';
import { GAME_VERSION } from './VersionDisplay';

/**
 * Main tabs for customization
 */
enum MainTab {
	SKINS = 'skins',
	ACCESSORIES = 'accessories',
}

/**
 * Offline game mode for Play vs AI
 */
export enum OfflineGameMode {
	CLASSIC = 'classic',
	NIGHTMARE = 'nightmare',
	ZEN = 'zen',
}

/**
 * Event handlers for ConnectionUI
 */
export interface ConnectionUIEvents {
	onConnect: (name: string, color: number, skin?: SkinData, accessory?: AccessoryType) => void;
	onOfflinePlay: (skin?: SkinData, accessory?: AccessoryType, mode?: OfflineGameMode) => void;
	onTestModePlay?: (skin?: SkinData, accessory?: AccessoryType, config?: TestModeConfig) => void;
	onLocalParty?: () => void;
}

/**
 * ConnectionUI - Full-screen immersive home screen with 2-level tab system
 */
export class ConnectionUI {
	/** Container element */
	private container!: HTMLDivElement;

	/** UI elements */
	private nameInput!: HTMLInputElement;
	private connectButton!: HTMLButtonElement;
	private offlineButton!: HTMLButtonElement;
	private statusText!: HTMLParagraphElement;

	/** Skin selector component */
	private skinSelector!: SkinSelector;

	/** Accessory selector component */
	private accessorySelector!: AccessorySelector;

	/** Main tab buttons */
	private mainTabButtons: Map<MainTab, HTMLButtonElement> = new Map();

	/** Main tab panels */
	private mainTabPanels: Map<MainTab, HTMLDivElement> = new Map();

	/** Currently active main tab */
	private activeMainTab: MainTab = MainTab.SKINS;

	/** Sub-tab buttons container */
	private subTabContainer!: HTMLDivElement;

	/** Skins sub-tab buttons */
	private skinsSubTabButtons: Map<SkinTab, HTMLButtonElement> = new Map();

	/** Selected offline game mode */
	private selectedOfflineMode: OfflineGameMode = OfflineGameMode.CLASSIC;

	/** Mode toggle button */
	private modeToggle: HTMLButtonElement | null = null;

	/** Test Mode button */
	private testModeButton: HTMLButtonElement | null = null;

	/** Test Mode selector component */
	private testModeSelector: TestModeSelector | null = null;

	/** Local Party button */
	private localPartyButton: HTMLButtonElement | null = null;

	/** Local Party lobby component */
	private localPartyLobby: LocalPartyLobby | null = null;

	/** Signaling provider for Local Party mode */
	private signalingProvider: SignalingProvider | null = null;

	/** Event handlers */
	private events: ConnectionUIEvents;

	/** Whether UI is visible */
	private visible: boolean = false;

	/** Player count display element */
	private playerCountDisplay: HTMLDivElement | null = null;

	/** Stats refresh interval */
	private statsInterval: ReturnType<typeof setInterval> | null = null;

	constructor(events: ConnectionUIEvents) {
		this.events = events;
		this.createUI();
	}

	private createUI(): void {
		// Create CSS animations
		const style = document.createElement('style');
		style.textContent = `
			@keyframes ui-glow {
				0%, 100% { text-shadow: 0 0 20px rgba(78, 205, 196, 0.4); }
				50% { text-shadow: 0 0 35px rgba(78, 205, 196, 0.8), 0 0 10px rgba(78, 205, 196, 0.5); }
			}
			@keyframes ui-slide-up {
				from { opacity: 0; transform: translateY(30px); }
				to { opacity: 1; transform: translateY(0); }
			}
			@keyframes selection-bounce {
				0% { transform: scale(1); }
				30% { transform: scale(1.12); }
				60% { transform: scale(0.95); }
				100% { transform: scale(1); }
			}
			@keyframes play-pulse {
				0%, 100% { box-shadow: 0 4px 20px rgba(78, 205, 196, 0.4); }
				50% { box-shadow: 0 8px 35px rgba(78, 205, 196, 0.6); }
			}
			@keyframes tab-slide-in {
				from { opacity: 0; transform: translateY(10px) scale(0.98); }
				to { opacity: 1; transform: translateY(0) scale(1); }
			}
			@keyframes tab-bounce {
				0% { transform: scale(1); }
				30% { transform: scale(1.05); }
				60% { transform: scale(0.98); }
				100% { transform: scale(1); }
			}
		`;
		document.head.appendChild(style);

		// Container - Full screen overlay
		this.container = document.createElement('div');
		this.container.id = 'connection-ui';
		this.container.style.cssText = `
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			display: none;
			flex-direction: column;
			align-items: center;
			background: radial-gradient(circle at center, rgba(10, 10, 26, 0.85), rgba(5, 5, 15, 0.98));
			backdrop-filter: blur(8px);
			-webkit-backdrop-filter: blur(8px);
			z-index: 1000;
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			overflow-y: auto;
			padding: 20px;
			box-sizing: border-box;
		`;

		// Main Card Container - Single column layout
		const card = document.createElement('div');
		card.style.cssText = `
			width: 100%;
			max-width: 700px;
			background: rgba(25, 25, 40, 0.65);
			backdrop-filter: blur(16px);
			-webkit-backdrop-filter: blur(16px);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 32px;
			padding: 32px;
			box-shadow:
				0 20px 50px rgba(0, 0, 0, 0.5),
				inset 0 1px 0 rgba(255, 255, 255, 0.1);
			display: flex;
			flex-direction: column;
			gap: 24px;
			animation: ui-slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1);
		`;
		this.container.appendChild(card);

		// Add responsive styles
		const responsiveStyle = document.createElement('style');
		responsiveStyle.textContent = `
			@media (max-width: 600px) {
				#connection-ui > div { padding: 20px; border-radius: 24px; }
				#connection-ui h1 { font-size: 36px !important; }
			}
			@media (min-width: 900px) {
				#connection-ui > div { max-width: 900px; }
			}
			@media (min-width: 1200px) {
				#connection-ui > div { max-width: 1000px; }
			}
			@media (min-width: 1600px) {
				#connection-ui > div { max-width: 1100px; }
			}
		`;
		this.container.appendChild(responsiveStyle);

		// --- Header Section ---
		const header = document.createElement('div');
		header.style.cssText = `text-align: center;`;

		const title = document.createElement('h1');
		title.textContent = 'Woorms';
		title.style.cssText = `
			font-size: 52px;
			font-weight: 900;
			letter-spacing: -1px;
			margin: 0;
			background: linear-gradient(135deg, #4ecdc4 0%, #a78bfa 50%, #f472b6 100%);
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			filter: drop-shadow(0 4px 12px rgba(78, 205, 196, 0.3));
			animation: ui-glow 4s ease-in-out infinite;
		`;
		header.appendChild(title);

		const subtitle = document.createElement('p');
		subtitle.textContent = 'Eat. Grow. Dominate.';
		subtitle.style.cssText = `
			color: rgba(255, 255, 255, 0.6);
			font-size: 15px;
			margin: 8px 0 0 0;
			font-weight: 500;
			letter-spacing: 0.5px;
		`;
		header.appendChild(subtitle);

		// Player count display
		this.playerCountDisplay = document.createElement('div');
		this.playerCountDisplay.style.cssText = `
			display: inline-flex;
			align-items: center;
			gap: 6px;
			margin-top: 12px;
			padding: 6px 14px;
			background: rgba(78, 205, 196, 0.1);
			border: 1px solid rgba(78, 205, 196, 0.2);
			border-radius: 20px;
			font-size: 13px;
			color: rgba(255, 255, 255, 0.7);
		`;
		this.playerCountDisplay.innerHTML = `
			<span style="color: #4ecdc4;">●</span>
			<span class="player-count">-- players online</span>
		`;
		header.appendChild(this.playerCountDisplay);

		// Version display
		const versionDisplay = document.createElement('div');
		versionDisplay.style.cssText = `
			margin-top: 8px;
			font-size: 11px;
			color: rgba(255, 255, 255, 0.4);
			letter-spacing: 0.5px;
		`;
		versionDisplay.textContent = `v${GAME_VERSION}`;
		header.appendChild(versionDisplay);

		card.appendChild(header);

		// --- Large Worm Preview (Hero Section) ---
		this.skinSelector = new SkinSelector({
			onSkinChange: () => {},
		});

		this.accessorySelector = new AccessorySelector({
			onAccessoryChange: (accessory: AccessoryType) => {
				this.skinSelector.setAccessory(accessory);
			},
		});

		const previewContainer = document.createElement('div');
		previewContainer.classList.add('preview-container');
		previewContainer.style.cssText = `
			background: rgba(0, 0, 0, 0.3);
			border-radius: 20px;
			padding: 0;
			overflow: hidden;
			border: 1px solid rgba(255, 255, 255, 0.05);
			min-height: 200px;
			max-height: 260px;
		`;
		const previewCanvas = this.skinSelector.getPreviewCanvas();
		previewCanvas.style.width = '100%';
		previewCanvas.style.height = '240px';
		previewCanvas.style.objectFit = 'cover';
		previewCanvas.style.display = 'block';
		previewContainer.appendChild(previewCanvas);
		card.appendChild(previewContainer);

		// Initialize accessory on skin selector
		this.skinSelector.setAccessory(this.accessorySelector.getSelectedAccessory());

		// --- Main Tabs (Skins / Accessories) ---
		const mainTabBar = document.createElement('div');
		mainTabBar.style.cssText = `
			display: flex;
			background: rgba(0, 0, 0, 0.25);
			padding: 6px;
			border-radius: 16px;
			gap: 8px;
		`;

		const createMainTabBtn = (tab: MainTab, label: string, icon: string) => {
			const btn = document.createElement('button');
			btn.innerHTML = `<span style="margin-right: 10px; font-size: 20px;">${icon}</span>${label}`;
			btn.style.cssText = `
				flex: 1;
				padding: 14px 20px;
				border: none;
				background: transparent;
				color: rgba(255, 255, 255, 0.5);
				font-weight: 700;
				font-size: 16px;
				border-radius: 12px;
				cursor: pointer;
				transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
				display: flex;
				align-items: center;
				justify-content: center;
			`;
			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.switchMainTab(tab);
			});
			btn.addEventListener('mouseover', () => {
				if (this.activeMainTab !== tab) {
					btn.style.background = 'rgba(78, 205, 196, 0.1)';
					btn.style.color = 'rgba(255, 255, 255, 0.7)';
				}
				SoundManager.getInstance().play(SoundType.UI_HOVER);
			});
			btn.addEventListener('mouseout', () => {
				if (this.activeMainTab !== tab) {
					btn.style.background = 'transparent';
					btn.style.color = 'rgba(255, 255, 255, 0.5)';
				}
			});
			this.mainTabButtons.set(tab, btn);
			mainTabBar.appendChild(btn);
		};

		createMainTabBtn(MainTab.SKINS, 'Skins', '🎨');
		createMainTabBtn(MainTab.ACCESSORIES, 'Accessories', '🎩');
		card.appendChild(mainTabBar);

		// --- Sub-tabs Container ---
		this.subTabContainer = document.createElement('div');
		this.subTabContainer.style.cssText = `
			display: flex;
			background: rgba(0, 0, 0, 0.15);
			padding: 4px;
			border-radius: 10px;
			gap: 4px;
		`;

		// Create skins sub-tabs (Colors, Patterns, Flags)
		const skinSubTabs: { id: SkinTab; label: string; icon: string }[] = [
			{ id: SkinTab.COLORS, label: 'Colors', icon: '🌈' },
			{ id: SkinTab.PATTERNS, label: 'Patterns', icon: '✨' },
			{ id: SkinTab.FLAGS, label: 'Flags', icon: '🏁' },
		];

		skinSubTabs.forEach((tab) => {
			const btn = document.createElement('button');
			btn.innerHTML = `<span style="margin-right: 6px">${tab.icon}</span>${tab.label}`;
			btn.style.cssText = `
				flex: 1;
				padding: 10px 12px;
				border: none;
				background: transparent;
				color: rgba(255, 255, 255, 0.5);
				font-weight: 600;
				font-size: 13px;
				border-radius: 8px;
				cursor: pointer;
				transition: all 0.2s ease;
			`;
			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.switchSkinsSubTab(tab.id);
			});
			btn.addEventListener('mouseover', () => {
				SoundManager.getInstance().play(SoundType.UI_HOVER);
			});
			this.skinsSubTabButtons.set(tab.id, btn);
			this.subTabContainer.appendChild(btn);
		});
		card.appendChild(this.subTabContainer);

		// --- Tab Panels ---
		const skinsPanel = document.createElement('div');
		skinsPanel.appendChild(this.skinSelector.getElement());
		this.mainTabPanels.set(MainTab.SKINS, skinsPanel);
		card.appendChild(skinsPanel);

		const accessoriesPanel = document.createElement('div');
		accessoriesPanel.style.display = 'none';
		accessoriesPanel.appendChild(this.accessorySelector.getElement());
		this.mainTabPanels.set(MainTab.ACCESSORIES, accessoriesPanel);
		card.appendChild(accessoriesPanel);

		// --- Play Section ---
		const playSection = document.createElement('div');
		playSection.style.cssText = `
			display: flex;
			flex-direction: column;
			gap: 16px;
			padding-top: 16px;
			border-top: 1px solid rgba(255, 255, 255, 0.08);
		`;

		// Name Input
		const inputGroup = document.createElement('div');
		const label = document.createElement('label');
		label.textContent = 'YOUR NICKNAME';
		label.style.cssText = `
			display: block;
			font-size: 11px;
			font-weight: 700;
			color: rgba(255, 255, 255, 0.4);
			margin-bottom: 8px;
			letter-spacing: 1.5px;
		`;
		inputGroup.appendChild(label);

		this.nameInput = document.createElement('input');
		this.nameInput.type = 'text';
		this.nameInput.placeholder = 'Enter Nickname';
		this.nameInput.maxLength = 16;
		this.nameInput.value = this.loadSavedName();
		this.nameInput.addEventListener('input', (e) => this.saveName((e.target as HTMLInputElement).value));
		this.nameInput.style.cssText = `
			width: 100%;
			padding: 16px 20px;
			font-size: 16px;
			font-weight: 600;
			background: rgba(0, 0, 0, 0.3);
			border: 2px solid rgba(255, 255, 255, 0.1);
			border-radius: 14px;
			color: #fff;
			outline: none;
			transition: all 0.2s ease;
			box-sizing: border-box;
		`;
		this.nameInput.addEventListener('focus', () => {
			this.nameInput.style.borderColor = '#4ecdc4';
			this.nameInput.style.background = 'rgba(0, 0, 0, 0.5)';
		});
		this.nameInput.addEventListener('blur', () => {
			this.nameInput.style.borderColor = 'rgba(255, 255, 255, 0.1)';
			this.nameInput.style.background = 'rgba(0, 0, 0, 0.3)';
		});
		this.nameInput.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') this.handleConnect();
		});
		inputGroup.appendChild(this.nameInput);
		playSection.appendChild(inputGroup);

		// Play Online Button (Large, prominent, pulsing)
		this.connectButton = document.createElement('button');
		this.connectButton.innerHTML = `
			<div style="font-size: 22px; font-weight: 800; margin-bottom: 4px;">🚀 Play Online</div>
			<div style="font-size: 12px; font-weight: 400; opacity: 0.8;">Join the cosmos arena</div>
		`;
		this.connectButton.style.cssText = `
			width: 100%;
			padding: 22px;
			border: none;
			border-radius: 18px;
			background: linear-gradient(135deg, #4ecdc4 0%, #2cb5e8 100%);
			color: #fff;
			font-weight: 800;
			font-size: 18px;
			cursor: pointer;
			box-shadow: 0 4px 20px rgba(78, 205, 196, 0.4);
			transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
			animation: play-pulse 2s ease-in-out infinite;
			text-align: center;
		`;
		this.connectButton.addEventListener('mouseover', () => {
			this.connectButton.style.transform = 'translateY(-3px) scale(1.02)';
			this.connectButton.style.boxShadow = '0 8px 35px rgba(78, 205, 196, 0.6)';
			this.connectButton.style.animation = 'none';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});
		this.connectButton.addEventListener('mouseout', () => {
			this.connectButton.style.transform = 'translateY(0) scale(1)';
			this.connectButton.style.boxShadow = '0 4px 20px rgba(78, 205, 196, 0.4)';
			this.connectButton.style.animation = 'play-pulse 2s ease-in-out infinite';
		});
		this.connectButton.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.handleConnect();
		});
		playSection.appendChild(this.connectButton);

		// Offline Row (Practice Offline + Mode Toggle)
		const offlineRow = document.createElement('div');
		offlineRow.style.cssText = `
			display: flex;
			gap: 12px;
			align-items: center;
		`;

		this.offlineButton = document.createElement('button');
		this.offlineButton.innerHTML = `<span style="margin-right:8px">🤖</span>Practice Offline`;
		this.offlineButton.style.cssText = `
			flex: 1;
			padding: 14px;
			border: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: 12px;
			background: rgba(255, 255, 255, 0.05);
			color: #fff;
			font-weight: 600;
			font-size: 14px;
			cursor: pointer;
			transition: all 0.2s ease;
		`;
		this.offlineButton.addEventListener('mouseover', () => {
			this.offlineButton.style.background = 'rgba(255, 255, 255, 0.1)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});
		this.offlineButton.addEventListener('mouseout', () => {
			this.offlineButton.style.background = 'rgba(255, 255, 255, 0.05)';
		});
		this.offlineButton.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.handleOfflinePlay();
		});
		offlineRow.appendChild(this.offlineButton);

		// Mode Toggle
		this.modeToggle = document.createElement('button');
		this.updateModeToggleStyle();
		this.modeToggle.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.toggleOfflineMode();
		});
		offlineRow.appendChild(this.modeToggle);

		// My World Button (custom solo game)
		this.testModeButton = document.createElement('button');
		this.testModeButton.innerHTML = '<span style="color:#a78bfa">🌍 My World</span>';
		this.testModeButton.style.cssText = `
			background: rgba(0,0,0,0.3);
			border: 1px solid rgba(167, 139, 250, 0.3);
			padding: 10px 16px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 700;
			cursor: pointer;
			color: #fff;
			transition: all 0.2s ease;
			white-space: nowrap;
		`;
		this.testModeButton.addEventListener('mouseover', () => {
			this.testModeButton!.style.background = 'rgba(167, 139, 250, 0.2)';
			this.testModeButton!.style.borderColor = 'rgba(167, 139, 250, 0.5)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});
		this.testModeButton.addEventListener('mouseout', () => {
			this.testModeButton!.style.background = 'rgba(0,0,0,0.3)';
			this.testModeButton!.style.borderColor = 'rgba(167, 139, 250, 0.3)';
		});
		this.testModeButton.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.showTestModeSelector();
		});
		offlineRow.appendChild(this.testModeButton);

		// Local Party Button
		this.localPartyButton = document.createElement('button');
		this.localPartyButton.innerHTML = '<span style="color:#f9ed69">🎉 Local Party</span>';
		this.localPartyButton.style.cssText = `
			background: rgba(0,0,0,0.3);
			border: 1px solid rgba(249, 237, 105, 0.3);
			padding: 10px 16px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 700;
			cursor: pointer;
			color: #fff;
			transition: all 0.2s ease;
			white-space: nowrap;
		`;
		this.localPartyButton.addEventListener('mouseover', () => {
			this.localPartyButton!.style.background = 'rgba(249, 237, 105, 0.2)';
			this.localPartyButton!.style.borderColor = 'rgba(249, 237, 105, 0.5)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});
		this.localPartyButton.addEventListener('mouseout', () => {
			this.localPartyButton!.style.background = 'rgba(0,0,0,0.3)';
			this.localPartyButton!.style.borderColor = 'rgba(249, 237, 105, 0.3)';
		});
		this.localPartyButton.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.showLocalPartyLobby();
		});
		offlineRow.appendChild(this.localPartyButton);

		playSection.appendChild(offlineRow);
		card.appendChild(playSection);

		// Status Text
		this.statusText = document.createElement('p');
		this.statusText.style.cssText = `
			color: #ff6b6b;
			font-size: 13px;
			min-height: 20px;
			text-align: center;
			margin: 0;
		`;
		card.appendChild(this.statusText);

		// --- Settings Bar (Bottom) ---
		const settingsBar = document.createElement('div');
		settingsBar.style.cssText = `
			display: flex;
			gap: 12px;
			justify-content: center;
			align-items: center;
			padding-top: 16px;
			border-top: 1px solid rgba(255, 255, 255, 0.08);
		`;

		// Eye Protection - cycles through: Normal -> Eye Shield -> Eye Shield+ -> Normal
		const eyeBtn = this.createSettingsButton('✨', 'Eye Comfort');
		const updateEyeBtn = (level: EyeProtectionLevel) => {
			this.updateEyeButtonState(eyeBtn, level);
			// Update play button animation based on setting (animations only in Normal mode)
			const animationsEnabled = level === EyeProtectionLevel.OFF;
			this.connectButton.style.animation = animationsEnabled ? 'play-pulse 2s ease-in-out infinite' : 'none';
		};
		eyeBtn.addEventListener('click', () => {
			const settings = EyeProtectionSettings.getInstance();
			const currentLevel = settings.getLevel();
			const nextLevel = (currentLevel + 1) % 3;
			settings.setLevel(nextLevel);
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			updateEyeBtn(nextLevel);
		});
		updateEyeBtn(EyeProtectionSettings.getInstance().getLevel());
		settingsBar.appendChild(eyeBtn);

		// Sound - compact button for settings bar
		const soundBtn = this.createSettingsButton('🔊', 'Sound');
		const soundManager = SoundManager.getInstance();
		this.updateSoundButtonState(soundBtn, !soundManager.muted);
		soundBtn.addEventListener('click', () => {
			const newMuted = soundManager.toggleMute();
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.updateSoundButtonState(soundBtn, !newMuted);
		});
		settingsBar.appendChild(soundBtn);

		// Help
		const helpBtn = this.createSettingsButton('❓', 'Help');
		helpBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			getHelpMenu().show();
		});
		settingsBar.appendChild(helpBtn);

		// Mobile Controls (only on touch devices)
		if (isTouchDevice()) {
			const mobileBtn = this.createSettingsButton('🎮', 'Mobile Controls');
			mobileBtn.style.borderColor = 'rgba(167, 139, 250, 0.3)';
			mobileBtn.style.background = 'rgba(167, 139, 250, 0.15)';
			mobileBtn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				const mobileSettings = getMobileControlsSettings({
					onClose: () => {
						// Modal closed
					},
				});
				mobileSettings.show();
			});
			mobileBtn.addEventListener('mouseover', () => {
				mobileBtn.style.borderColor = 'rgba(167, 139, 250, 0.5)';
				mobileBtn.style.background = 'rgba(167, 139, 250, 0.25)';
				mobileBtn.style.transform = 'scale(1.1)';
				SoundManager.getInstance().play(SoundType.UI_HOVER);
			});
			mobileBtn.addEventListener('mouseout', () => {
				mobileBtn.style.borderColor = 'rgba(167, 139, 250, 0.3)';
				mobileBtn.style.background = 'rgba(167, 139, 250, 0.15)';
				mobileBtn.style.transform = 'scale(1)';
			});
			settingsBar.appendChild(mobileBtn);
		}

		card.appendChild(settingsBar);

		document.body.appendChild(this.container);

		// Set initial tab states
		this.switchMainTab(MainTab.SKINS);
		this.switchSkinsSubTab(SkinTab.COLORS);
	}

	/**
	 * Create a compact settings button for the bottom bar
	 */
	private createSettingsButton(icon: string, title: string): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.innerHTML = icon;
		btn.title = title;
		btn.style.cssText = `
			width: 44px;
			height: 44px;
			border-radius: 12px;
			border: 1px solid rgba(255, 255, 255, 0.1);
			background: rgba(255, 255, 255, 0.05);
			color: #fff;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 20px;
			transition: all 0.2s ease;
		`;
		btn.addEventListener('mouseover', () => {
			btn.style.background = 'rgba(255, 255, 255, 0.15)';
			btn.style.transform = 'scale(1.1)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});
		btn.addEventListener('mouseout', () => {
			btn.style.background = 'rgba(255, 255, 255, 0.05)';
			btn.style.transform = 'scale(1)';
		});
		return btn;
	}

	/**
	 * Update eye protection button visual state
	 */
	private updateEyeButtonState(btn: HTMLButtonElement, level: EyeProtectionLevel): void {
		const config = {
			[EyeProtectionLevel.OFF]: { icon: '✨', border: 'rgba(78, 205, 196, 0.3)', bg: 'rgba(78, 205, 196, 0.15)', title: 'Normal Mode' },
			[EyeProtectionLevel.MILD]: { icon: '👁️', border: 'rgba(249, 237, 105, 0.4)', bg: 'rgba(249, 237, 105, 0.15)', title: 'Eye Shield (no animations)' },
			[EyeProtectionLevel.STRONG]: { icon: '🛡️', border: 'rgba(255, 107, 107, 0.4)', bg: 'rgba(255, 107, 107, 0.15)', title: 'Eye Shield+ (calm colors)' },
		};
		const c = config[level];
		btn.innerHTML = c.icon;
		btn.title = c.title;
		btn.style.borderColor = c.border;
		btn.style.background = c.bg;
	}

	/**
	 * Update sound button visual state
	 */
	private updateSoundButtonState(btn: HTMLButtonElement, enabled: boolean): void {
		btn.innerHTML = enabled ? '🔊' : '🔇';
		btn.style.borderColor = enabled ? 'rgba(78, 205, 196, 0.3)' : 'rgba(255, 255, 255, 0.1)';
		btn.style.background = enabled ? 'rgba(78, 205, 196, 0.15)' : 'rgba(255, 255, 255, 0.05)';
	}

	/**
	 * Switch main tab (Skins / Accessories)
	 */
	private switchMainTab(tab: MainTab): void {
		if (this.activeMainTab === tab && this.visible) return;

		this.activeMainTab = tab;

		// Update main tab button styles with bounce animation
		const animationsEnabled = EyeProtectionSettings.getInstance().areAnimationsEnabled();
		this.mainTabButtons.forEach((btn, t) => {
			if (t === tab) {
				btn.style.background = 'rgba(78, 205, 196, 0.2)';
				btn.style.color = '#4ecdc4';
				btn.style.boxShadow = '0 0 0 2px rgba(78, 205, 196, 0.3)';
				if (animationsEnabled) {
					btn.style.animation = 'tab-bounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
					setTimeout(() => { btn.style.animation = ''; }, 400);
				}
			} else {
				btn.style.background = 'transparent';
				btn.style.color = 'rgba(255, 255, 255, 0.5)';
				btn.style.boxShadow = 'none';
			}
		});

		// Update tab panels with slide animation
		this.mainTabPanels.forEach((panel, t) => {
			if (t === tab) {
				panel.style.display = 'block';
				if (animationsEnabled) {
					panel.style.animation = 'tab-slide-in 0.3s ease-out';
				}
			} else {
				panel.style.display = 'none';
			}
		});

		// Show/hide sub-tabs based on main tab
		if (tab === MainTab.SKINS) {
			this.subTabContainer.style.display = 'flex';
		} else {
			this.subTabContainer.style.display = 'none';
		}
	}

	/**
	 * Switch skins sub-tab (Colors / Patterns / Flags)
	 */
	private switchSkinsSubTab(tab: SkinTab): void {
		// Update sub-tab button styles
		const animationsEnabled = EyeProtectionSettings.getInstance().areAnimationsEnabled();
		this.skinsSubTabButtons.forEach((btn, t) => {
			if (t === tab) {
				btn.style.background = 'rgba(78, 205, 196, 0.15)';
				btn.style.color = '#4ecdc4';
				btn.style.boxShadow = '0 0 0 1px rgba(78, 205, 196, 0.25)';
				if (animationsEnabled) {
					btn.style.animation = 'selection-bounce 0.3s ease-out';
					setTimeout(() => { btn.style.animation = ''; }, 300);
				}
			} else {
				btn.style.background = 'transparent';
				btn.style.color = 'rgba(255, 255, 255, 0.5)';
				btn.style.boxShadow = 'none';
			}
		});

		// Tell skin selector to switch tabs
		this.skinSelector.switchTab(tab);
	}

	private toggleOfflineMode(): void {
		const modes = [OfflineGameMode.CLASSIC, OfflineGameMode.NIGHTMARE];
		const currentIndex = modes.indexOf(this.selectedOfflineMode);
		const nextIndex = (currentIndex + 1) % modes.length;
		this.selectedOfflineMode = modes[nextIndex] ?? OfflineGameMode.CLASSIC;
		this.updateModeToggleStyle();
	}

	private updateModeToggleStyle(): void {
		if (!this.modeToggle) return;

		const isNightmare = this.selectedOfflineMode === OfflineGameMode.NIGHTMARE;

		this.modeToggle.innerHTML = isNightmare
			? '<span style="color:#ff6b6b">💀 Nightmare</span>'
			: '<span style="color:#4ecdc4">🎮 Classic</span>';

		this.modeToggle.style.cssText = `
			background: rgba(0,0,0,0.3);
			border: 1px solid ${isNightmare ? 'rgba(255, 107, 107, 0.3)' : 'rgba(78, 205, 196, 0.3)'};
			padding: 10px 16px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 700;
			cursor: pointer;
			color: #fff;
			transition: all 0.2s ease;
			white-space: nowrap;
		`;
	}

	private handleConnect(): void {
		const name = this.nameInput.value.trim() || 'Player';
		this.saveName(name);
		this.events.onConnect(
			name,
			this.skinSelector.getPrimaryColorNumber(),
			this.skinSelector.getSkinData(),
			this.accessorySelector.getSelectedAccessory()
		);
	}

	private handleOfflinePlay(): void {
		this.events.onOfflinePlay(
			this.skinSelector.getSkinData(),
			this.accessorySelector.getSelectedAccessory(),
			this.selectedOfflineMode
		);
	}

	private showTestModeSelector(): void {
		// Create test mode selector if it doesn't exist
		if (!this.testModeSelector) {
			this.testModeSelector = new TestModeSelector({
				onConfigChange: () => {
					// Optional: could preview changes
				},
				onStartGame: (config: TestModeConfig) => {
					this.handleTestModePlay(config);
				},
				onCancel: () => {
					this.testModeSelector?.hide();
				},
			});
		}
		this.testModeSelector.show();
	}

	private handleTestModePlay(config: TestModeConfig): void {
		this.testModeSelector?.hide();
		if (this.events.onTestModePlay) {
			this.events.onTestModePlay(
				this.skinSelector.getSkinData(),
				this.accessorySelector.getSelectedAccessory(),
				config
			);
		}
	}

	private showLocalPartyLobby(): void {
		// Create local party lobby if it doesn't exist
		if (!this.localPartyLobby) {
			this.localPartyLobby = new LocalPartyLobby({
				onHostGame: async () => {
					// Create signaling provider and host a room
					try {
						this.signalingProvider = new PeerJSSignalingProvider();
						const roomCode = await this.signalingProvider.createRoom();
						this.localPartyLobby?.showHostView(roomCode);
					} catch (error) {
						alert('Failed to create room. Please try again.');
					}
				},
				onJoinGame: async (roomCode: string) => {
					// Create signaling provider and join room
					try {
						this.signalingProvider = new PeerJSSignalingProvider();
						await this.signalingProvider.joinRoom(roomCode);
						// TODO: Show waiting/lobby view for client
					} catch (error) {
						alert('Failed to join room. Check the room code and try again.');
					}
				},
				onLeave: () => {
					// Disconnect from signaling provider
					this.signalingProvider?.disconnect();
					this.signalingProvider = null;
					this.localPartyLobby?.hide();
				},
				onStartGame: () => {
					// Trigger the local party event
					if (this.events.onLocalParty) {
						this.events.onLocalParty();
					}
				},
				onConfigChange: () => {
					// Config changes handled internally
				},
				onReadyChange: () => {
					// Ready state handled internally
				},
				onBack: () => {
					// Disconnect from signaling provider if connected
					this.signalingProvider?.disconnect();
					this.signalingProvider = null;
					this.localPartyLobby?.hide();
				},
			});
		}
		this.localPartyLobby.showJoinView();
	}

	private loadSavedName(): string {
		return localStorage.getItem('worm_name') || '';
	}

	private saveName(name: string): void {
		localStorage.setItem('worm_name', name);
	}

	// --- Public API ---

	show(): void {
		this.visible = true;
		this.container.style.display = 'flex';
		this.nameInput.focus();
		this.setStatus('');
		this.skinSelector.startAnimation();
		this.startStatsRefresh();
	}

	hide(): void {
		this.visible = false;
		this.container.style.display = 'none';
		this.skinSelector.stopAnimation();
		this.stopStatsRefresh();
	}

	/**
	 * Fetch and display player stats from server
	 */
	private async fetchStats(): Promise<void> {
		try {
			// Use the same server URL logic as NetworkManager
			const serverUrl = (import.meta as any).env?.VITE_SERVER_URL || 'https://wooorms-server.fly.dev';
			const response = await fetch(`${serverUrl}/stats`);
			if (response.ok) {
				const data = await response.json();
				this.updatePlayerCount(data.players);
			}
		} catch {
			// Silently fail - just show default text
		}
	}

	/**
	 * Update player count display
	 */
	private updatePlayerCount(count: number): void {
		if (this.playerCountDisplay) {
			const countSpan = this.playerCountDisplay.querySelector('.player-count');
			if (countSpan) {
				countSpan.textContent = count === 1 ? '1 player online' : `${count} players online`;
			}
		}
	}

	/**
	 * Start periodic stats refresh
	 */
	private startStatsRefresh(): void {
		this.fetchStats();
		this.statsInterval = setInterval(() => this.fetchStats(), 10000); // Refresh every 10s
	}

	/**
	 * Stop stats refresh
	 */
	private stopStatsRefresh(): void {
		if (this.statsInterval) {
			clearInterval(this.statsInterval);
			this.statsInterval = null;
		}
	}

	isVisible(): boolean {
		return this.visible;
	}

	updateConnectionState(state: ConnectionState): void {
		switch (state) {
			case ConnectionState.CONNECTING:
				this.setStatus('Connecting...', '#4ecdc4');
				this.setButtonsEnabled(false);
				break;
			case ConnectionState.CONNECTED:
				this.setStatus('Connected!', '#4ecdc4');
				break;
			case ConnectionState.RECONNECTING:
				this.setStatus('Reconnecting...', '#f38181');
				this.setButtonsEnabled(false);
				break;
			case ConnectionState.ERROR:
				this.setStatus('Connection failed.', '#ff6b6b');
				this.setButtonsEnabled(true);
				break;
			case ConnectionState.DISCONNECTED:
				this.setStatus('');
				this.setButtonsEnabled(true);
				break;
		}
	}

	setStatus(text: string, color: string = '#888'): void {
		this.statusText.textContent = text;
		this.statusText.style.color = color;
	}

	private setButtonsEnabled(enabled: boolean): void {
		this.connectButton.disabled = !enabled;
		this.offlineButton.disabled = !enabled;
		this.connectButton.style.opacity = enabled ? '1' : '0.5';
		this.connectButton.style.filter = enabled ? 'none' : 'grayscale(100%)';
		this.connectButton.style.animation = enabled ? 'play-pulse 2s ease-in-out infinite' : 'none';
		this.offlineButton.style.opacity = enabled ? '1' : '0.5';
		this.connectButton.style.cursor = enabled ? 'pointer' : 'not-allowed';
		this.offlineButton.style.cursor = enabled ? 'pointer' : 'not-allowed';
	}

	destroy(): void {
		this.stopStatsRefresh();
		this.container.remove();
	}
}
